
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import {PermissionsGroup} from '@/store/models.def';
import echarts from 'echarts';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import {GobiColor} from '@/helpers/color';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
  components: {
    IncludeDebitNoteToggle,
    IncludeCreditNoteToggle,
    SlPhModuleOnlyToggle,
    SecondaryLineBarChartFilterable,
  },
})
export default class SalesTotalSalesByRegionBarChart extends FilterWidget {
  public get permissionIds(): PermissionsGroup[] {
    return ['sales', 'customers'];
  }

  public dataZoom = [
    {
      type: 'slider',
    },
    {
      type: 'inside',
    },
  ];

  public filterIds: Array<
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'compareDate1'
      | 'compareDate2'
      | 'comparisonPeriod'
  > = [
    'customers',
    'agents',
    'compareDate1',
    'compareDate2',
    'comparisonPeriod',
  ];

  public xAxisData: string[] = [];
  public series: echarts.ECharts[] | any = [];

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get yAxisLabel2() {
    let label: string = '';

    label = this.currencySymbol;

    return label;
  }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get includeCreditNote() {
    return settingx.now.includeCreditNote;
  }

  public get includeDebitNote() {
    return settingx.now.includeDebitNote;
  }

  public get slPhModuleOnly() {
    return settingx.now.slPhModuleOnly;
  }

  public get expensiveHook() {
    const {
        selectedDateRange,
        includeDebitNote,
        includeCreditNote,
        slPhModuleOnly,
    } = this;
    return JSON.stringify([
        selectedDateRange,
        includeDebitNote,
        includeCreditNote,
        slPhModuleOnly,
    ]);
  }

  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.35);

    const queryHead = await this._ensureTablesLoaded(
      ['globalArea', 'sales', 'creditNotes', 'customerTrades'] as any[],
    );
    this.checkInterrupted(jobId);

    const areaTable = getTableName('globalArea');
    const salesTable = getTableName('sales');
    const cnTable = getTableName('creditNotes');
    const arTable = getTableName('customerTrades');

    const areaRows = await queryHead.rawQuery(`
      SELECT "CODE" as code, "DESCRIPTION" as descr FROM ${areaTable}
    `);
    const areaMap = new Map<string, string>();
    for (const r of areaRows) { areaMap.set(String(r.code), String(r.descr)); }
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.35, 0.9);

    const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

    interface ProcessedAreaData { text: string; sum: number; }
    const processedData: ProcessedAreaData[] = [];

    if (this.slPhModuleOnly) {
      const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
      const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

      const [salesByArea, cnByArea] = await Promise.all([
        queryHead.rawQuery(`
          SELECT "AREA" as area, SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
          FROM ${salesTable}
          WHERE ${dateFilter}${docTypeFilter} GROUP BY "AREA"
        `),
        queryHead.rawQuery(`
          SELECT "AREA" as area, SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
          FROM ${cnTable}
          WHERE ${dateFilter} GROUP BY "AREA"
        `),
      ]);

      const cnMap = new Map<string, number>();
      for (const r of cnByArea) { cnMap.set(String(r.area), Number(r.totalAmt) || 0); }

      for (const r of salesByArea) {
        const area = String(r.area);
        if (!area) { continue; }
        const salesAmt = Number(r.totalAmt) || 0;
        const cnAmt = this.includeCreditNote ? (cnMap.get(area) || 0) : 0;
        processedData.push({ text: areaMap.get(area) || area, sum: salesAmt - cnAmt });
      }
    } else {
      const arByArea = await queryHead.rawQuery(`
        SELECT "AREA" as area,
          SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as ivAmt,
          SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dnAmt,
          SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cnAmt
        FROM ${arTable}
        WHERE ${dateFilter} GROUP BY "AREA"
      `);

      for (const r of arByArea) {
        const area = String(r.area);
        if (!area) { continue; }
        const ivAmt = Number(r.ivAmt) || 0;
        const dnAmt = this.includeDebitNote ? (Number(r.dnAmt) || 0) : 0;
        const cnAmt = this.includeCreditNote ? (Number(r.cnAmt) || 0) : 0;
        processedData.push({ text: areaMap.get(area) || area, sum: ivAmt + dnAmt - cnAmt });
      }
    }
    this.generateLoadingText(0.95);

    processedData.sort((a, b) => b.sum - a.sum);

    this.xAxisData = processedData.map((dd) => dd.text);
    this.series = [{
      name: 'This Year', type: 'bar', smooth: true,
      barWidth: '40%', color: GobiColor.COLOR1,
      data: processedData.map((dd) => dd.sum),
    }];

    this.saveHistory('xAxisData', 'series');
  }
}
